import React, { useState, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import BaseLayout from './layouts/BaseLayout';
import ContentBackground from './components/ContentBackground';
import Sidebar from './components/Sidebar';
import SearchBar from './components/SearchBar';
import SearchResultCards from './components/SearchResultCards';
import ProfilePhoto from './components/ProfilePhoto';
import ScaylorSearchCard from './components/ScaylorSearchCard';
import Login from './components/Login';
import Settings from './components/Settings';
import Register from './components/Register';
import OrganizationSetup from './components/OrganizationSetup';
import ProfileSetup from './components/ProfileSetup';
import PricingTable from './components/PricingTable';
import AcceptInvite from './components/AcceptInvite';
import ScaylorOrbB from './assets/images/ScaylorOrbB.svg';

function App() {
  const [searchResults, setSearchResults] = useState([
    {
      title: "Wyatt Motorsports Q1 2023 Cash Flow Statement",
      type: "Excel",
      description: "This spreadsheet provides a detailed cash flow statement for Wyatt Motorsports for the first quarter of 2023. It includes a breakdown of operating cash flow (e.g., revenue from sales and operational expenses), investing cash flow (e.g., asset purchases and sales), and financing cash flow (e.g., debt repayments and equity financing)."
    },
    {
      title: "Wyatt Motorsports Monthly Cash Flow (May 2023)",
      type: "PDF",
      description: "This monthly cash flow report provides an in-depth overview of cash activities for May 2023. It includes a granular analysis of revenue from events, merchandise sales, and service contracts. The report also breaks down expenses such as payroll, vendor payments, and one-time maintenance costs. Anomalies such as a 15% increase in promotional "
    },
    {
      title: "Wyatt Motorsports Projected Cash Flow for New Facility Expansion",
      type: "PowerPoint",
      description: "This presentation outlines the projected cash flow implications of the planned facility expansion project. It includes detailed forecasts of construction costs, equipment purchases, and anticipated revenue increases from expanded operations. The analysis covers a 5-year period, with quarterly breakdowns of expected cash inflows and outflows. "
    }
  ]);

  const [scaylorAnswer] = useState({
    answer: "Based on the available documents, Wyatt Motorsports is experiencing significant cash flow activities in 2023, with detailed reporting across Q1 and monthly statements. The company is planning a facility expansion project with projected cash flow implications over a 5-year period.",
    documents: [
      {
        title: "Q1 2023 Cash Flow Statement",
        type: "Excel",
        url: "#"
      },
      {
        title: "May 2023 Monthly Cash Flow",
        type: "PDF",
        url: "#"
      },
      {
        title: "Facility Expansion Projections",
        type: "PowerPoint",
        url: "#"
      }
    ]
  });

  const [selectedContextId, setSelectedContextId] = useState(null);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const searchResultsRef = useRef(null);
  const scaylorSearchRef = useRef(null);

  const handleSearchComplete = (query) => {
    setCurrentSearchQuery(query);
    
    // Create a promise for each component's loading
    const promises = [
      new Promise(resolve => {
        if (searchResultsRef.current && searchResultsRef.current.fetchLatestMessages) {
          searchResultsRef.current.fetchLatestMessages().then(resolve);
        } else {
          resolve();
        }
      }),
      new Promise(resolve => {
        if (scaylorSearchRef.current && scaylorSearchRef.current.fetchLatestMessage) {
          scaylorSearchRef.current.fetchLatestMessage().then(resolve);
        } else {
          resolve();
        }
      })
    ];

    // When both components are done loading, set loading to false
    Promise.all(promises).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/organization-setup" element={<OrganizationSetup />} />
        <Route path="/profile-setup" element={<ProfileSetup />} />
        <Route path="/pricing" element={<PricingTable />} />
        <Route path="/" element={
          <div className="flex">
            <Sidebar setSelectedContextId={setSelectedContextId} />
            <BaseLayout>
              <ContentBackground>
                <ProfilePhoto />
                <SearchBar 
                  selectedContextId={selectedContextId} 
                  onSearchComplete={handleSearchComplete}
                  setLoading={setIsLoading}
                />
                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex justify-center items-center py-12">
                    <img
                      src={ScaylorOrbB}
                      alt="Loading..."
                      className="w-24 h-24 animate-spin"
                    />
                  </div>
                )}
                <ScaylorSearchCard 
                  ref={scaylorSearchRef}
                  selectedContextId={selectedContextId}
                  searchQuery={currentSearchQuery}
                />
                <SearchResultCards 
                  ref={searchResultsRef}
                  selectedContextId={selectedContextId}
                />
              </ContentBackground>
            </BaseLayout>
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
